import { TfiClose } from "react-icons/tfi";
import { Sheet } from "react-modal-sheet";

import { useLaunchParams } from "@tma.js/sdk-react";

import { useBlumResultQuery } from "shared/api/use-blum-result-query";
import { useBlumScoreQuery } from "shared/api/use-blum-score-query";
import { useIssueBlumCertMutation } from "shared/api/use-issue-blum-cert-mutation";
import { useCompleteTaskMutation } from "shared/tg/hooks/use-complete-task-mutation";
import { useStoreCertificateMutation } from "shared/tg/hooks/use-store-certificate-mutation";
import { CertificateMetadata } from "shared/tg/types";
import { HolderCommitment } from "shared/tg/utils/generate-holder-commitment";
import { Spinner } from "shared/ui/spinner";
import { cn } from "shared/utils/cn";

import bgPng from "../assets/bg.png";
import { ErrorComponent } from "./error";
import { NoScore } from "./no-score";
import { PrefetchComponent } from "./prefetch-component";
import { Score } from "./score";
import { Success } from "./success";

type Props = {
  isOpen: boolean;
  holder: HolderCommitment;
  onClose: () => void;
  onSuccess?: (certs: CertificateMetadata[]) => void;
};

export const GetBlumCertificateSheet = (props: Props) => {
  const { isOpen, onClose, onSuccess, holder } = props;
  const { tgWebAppData } = useLaunchParams();
  const scoreQuery = useBlumScoreQuery(
    { userId: tgWebAppData?.user?.id },
    { enabled: isOpen }
  );

  const mutation = useIssueBlumCertMutation();

  const completeTaskMutation = useCompleteTaskMutation();
  const storeCertificate = useStoreCertificateMutation();

  const blumResultQuery = useBlumResultQuery({ id: mutation.data?.id });
  if (blumResultQuery.error) {
    console.error(blumResultQuery.error);
  }

  const handleIssue = () => {
    mutation.mutate(holder);
  };

  const handleClaim = async () => {
    if (!mutation.data?.id) return;
    try {
      const certs = await storeCertificate.mutateAsync({
        certificateId: mutation.data.id,
        url: `${import.meta.env.VITE_BLUM_GUARDIAN_URL}/certificate?id=${mutation.data.id}`,
        standard: "gip2",
      });

      await completeTaskMutation.mutateAsync({
        systemName: "generate-gip2-cert",
      });

      onSuccess?.(certs);
      onClose();
    } catch {
      //
    }
  };

  const isProcessing = Boolean(
    blumResultQuery.error?.message.includes("not ready") ||
      blumResultQuery.isFetching
  );

  const error = mutation.error || blumResultQuery.error;

  return (
    <>
      <PrefetchComponent />
      <Sheet detent="content" isOpen={isOpen} onClose={onClose}>
        <Sheet.Container>
          <Sheet.Header className="m-auto mt-5 flex w-full max-w-md items-center gap-x-12 px-6">
            <TfiClose
              onClick={onClose}
              className="ml-auto size-12 cursor-pointer p-2 brightness-125"
            />
          </Sheet.Header>
          <Sheet.Content className="pb-10">
            <div
              className={cn(
                "relative m-auto flex h-full w-full flex-col items-center overflow-hidden px-6"
                // "max-h-[calc(var(--tg-viewport-stable-height)-350px)]",
                // "min-h-[calc(var(--tg-viewport-stable-height)-350px)]"
              )}
            >
              <div
                className="place-center pointer-events-none -z-10 h-[500px] w-full bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${bgPng})`,
                }}
              />
              <div className="mx-auto flex w-full max-w-[300px] grow flex-col items-center">
                {scoreQuery.isPending && (
                  <Spinner className="place-center text-balticSea/50 size-10" />
                )}
                {scoreQuery.isError && <NoScore onClose={onClose} />}
                {scoreQuery.isSuccess &&
                  Boolean(!mutation.data?.id || isProcessing) && (
                    <Score
                      isLoading={mutation.isPending || isProcessing}
                      sybilScore={scoreQuery.data.sybilScore}
                      tradingScore={scoreQuery.data.activityScore}
                      onIssue={handleIssue}
                    />
                  )}

                {error && !isProcessing && (
                  <ErrorComponent
                    error={error}
                    onClose={() => {
                      mutation.reset();
                    }}
                  />
                )}

                {mutation.data && blumResultQuery.data && (
                  <Success
                    isLoading={storeCertificate.isPending}
                    onClaim={handleClaim}
                  />
                )}
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
    </>
  );
};
